<template>
  <b-modal
    :id="modalId"
    @show="onShow"
    hide-header
    hide-footer
    no-close-on-esc
    no-close-on-backdrop
    scrollable
    size="md"
    centered
  >
    <div>
      <p class="h3 text-capitalize">
        {{ $t("helpdesk.ticket.create_ticket") }}
      </p>
      <hr />
      <validation-observer v-slot="{ handleSubmit }">
        <b-form
          class="auth-login-form mt-2"
          @submit.prevent="handleSubmit(onSubmit)"
          ref="createTicket"
        >
          <!-- Clasificación: Siniestro / Incidente (catálogo) -->
          <validation-provider
            #default="{ errors }"
            :name="$t('helpdesk.ticket.sinister')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('helpdesk.ticket.sinister')"
              label-for="classification"
            >
              <v-select
                id="classification"
                v-model="form.classification"
                :options="classificationOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :reduce="(option) => option.value"
                label="label"
                :clearable="false"
                :placeholder="$t('helpdesk.ticket.sinister')"
                @input="onClassificationChange"
              />
              <p v-if="errors.length > 0" class="small text-danger">
                {{ errors[0] }}
              </p>
            </b-form-group>
          </validation-provider>

          <!-- Origen (nivel 1): solo siniestro (Humano / Desastre natural) -->
          <validation-provider
            v-if="withOrigin"
            #default="{ errors }"
            name="Origen"
            rules="required"
          >
            <b-form-group class="text-capitalize" label="Origen" label-for="failureOrigin">
              <v-select
                id="failureOrigin"
                v-model="form.failureOriginId"
                :options="originOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :reduce="(option) => option.value"
                label="label"
                :clearable="false"
                :loading="loadingCategories"
                placeholder="Origen"
                @input="onOriginChange"
              />
              <p v-if="errors.length > 0" class="small text-danger">
                {{ errors[0] }}
              </p>
            </b-form-group>
          </validation-provider>

          <!-- Tipo (nivel 2 del catálogo) -->
          <validation-provider
            #default="{ errors }"
            :name="$t('helpdesk.ticket.sinistertype')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('helpdesk.ticket.sinistertype')"
              label-for="failureType"
            >
              <v-select
                id="failureType"
                v-model="form.failureTypeId"
                :options="typeOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :reduce="(option) => option.value"
                label="label"
                :disabled="!form.classification || loadingCategories"
                :loading="loadingCategories"
                :placeholder="$t('helpdesk.ticket.sinistertype')"
                @input="form.failureSubtypeId = null"
              />
              <p v-if="errors.length > 0" class="small text-danger">
                {{ errors[0] }}
              </p>
            </b-form-group>
          </validation-provider>

          <!-- Subtipo (nivel 3); obligatorio solo si el tipo tiene subtipos -->
          <validation-provider
            #default="{ errors }"
            :name="$t('helpdesk.ticket.sinistersubtype')"
            :rules="subtypeOptions.length ? 'required' : ''"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('helpdesk.ticket.sinistersubtype')"
              label-for="failureSubtype"
            >
              <v-select
                id="failureSubtype"
                v-model="form.failureSubtypeId"
                :options="subtypeOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :reduce="(option) => option.value"
                label="label"
                :disabled="!subtypeOptions.length"
                :placeholder="$t('helpdesk.ticket.sinistersubtype')"
              />
              <p v-if="errors.length > 0" class="small text-danger">
                {{ errors[0] }}
              </p>
            </b-form-group>
          </validation-provider>

          <!-- Prioridad (helpdesk_priorities) -->
          <validation-provider
            #default="{ errors }"
            :name="$t('helpdesk.ticket.priority')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('helpdesk.ticket.priority')"
              label-for="priority"
            >
              <v-select
                id="priority"
                v-model="form.priority"
                :options="priorityOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :reduce="(option) => option.value"
                label="label"
                :placeholder="$t('helpdesk.ticket.priority')"
              />
              <p v-if="errors.length > 0" class="small text-danger">
                {{ errors[0] }}
              </p>
            </b-form-group>
          </validation-provider>

          <!-- Descripción -->
          <validation-provider
            :name="$t('helpdesk.ticket.description')"
            rules="required"
            v-slot="{ errors }"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('helpdesk.ticket.description')"
              label-for="message"
            >
              <counted-textarea
                id="message"
                v-model="form.message"
                rows="3"
                max-rows="6"
                :state="errors.length > 0 ? false : null"
                :maxlength="textLimits.description"
              />
              <p v-if="errors.length > 0" class="small text-danger">
                {{ errors[0] }}
              </p>
            </b-form-group>
          </validation-provider>

          <!-- PMI (unidades) - múltiple -->
          <validation-provider
            #default="{ errors }"
            :name="$t('helpdesk.ticket.relatedElement')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('helpdesk.ticket.relatedElement')"
              label-for="relatedElement"
            >
              <v-select
                id="relatedElement"
                v-model="form.unitIds"
                :options="unitOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :reduce="(option) => option.value"
                label="label"
                :filterable="false"
                :multiple="true"
                :loading="loadingUnits"
                :placeholder="$t('helpdesk.ticket.relatedElement')"
                @search="onUnitSearch"
                @input="onUnitsChange"
              />
              <p v-if="errors.length > 0" class="small text-danger">
                {{ errors[0] }}
              </p>
            </b-form-group>
          </validation-provider>

          <!-- Cámaras por cada PMI seleccionado (opcional) -->
          <b-form-group
            v-if="form.unitIds.length"
            class="text-capitalize"
            :label="$t('helpdesk.ticket.relatedCamera')"
            label-for="relatedCamera"
          >
            <b-form-checkbox
              v-model="form.hasRelatedCamera"
              name="has-related-camera"
              class="mr-0 mb-1"
              switch
              inline
              @change="onToggleCameras"
            >
              {{ $t("helpdesk.ticket.relatedCamera") }}
            </b-form-checkbox>

            <!-- Un selector de cámaras por cada PMI -->
            <template v-if="form.hasRelatedCamera">
              <div
                v-for="unitId in form.unitIds"
                :key="unitId"
                class="mb-1 pl-1 border-left"
              >
                <small class="text-muted d-block mb-25">
                  {{ unitLabel(unitId) }}
                </small>
                <v-select
                  :id="`relatedCamera-${unitId}`"
                  v-model="form.camerasByUnit[unitId]"
                  :options="camerasByUnitOptions[unitId] || []"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :reduce="(option) => option.value"
                  label="label"
                  :multiple="true"
                  :loading="!!loadingCamerasByUnit[unitId]"
                  :placeholder="$t('helpdesk.ticket.relatedCamera')"
                />
              </div>
            </template>
          </b-form-group>

          <!-- Adjuntos -->
          <file-upload-with-preview
            id="ticket_attachments"
            name="attachments"
            :label="$t('helpdesk.ticket.attachments')"
            :multiple="true"
            :disabled="buttonSave"
            :uploading="buttonSave"
            :on-update-files="onUpdateFiles"
            accepted-types="Imágenes, videos, PDF, Word, Excel, PowerPoint."
          />

          <b-button
            size="sm"
            type="submit"
            variant="primary"
            class="text-capitalize float-right"
            :disabled="buttonSave"
          >
            <span class="align-middle text-capitalize">
              {{ $t("actions.add") }}
            </span>
            <b-spinner v-if="buttonSave" small type="grow" class="ml-50" />
          </b-button>
          <b-button
            size="sm"
            type="button"
            variant="outline-secondary"
            @click="onCancel()"
            class="mr-1 text-capitalize float-right"
          >
            {{ $t("actions.cancel") }}
          </b-button>
        </b-form>
      </validation-observer>
    </div>
  </b-modal>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BSpinner,
  BModal,
} from "bootstrap-vue";
import store from "@/store";
import CountedTextarea from "@/views/helpdesk/CountedTextarea.vue";
import { TEXT_LIMITS } from "@/views/helpdesk/textLimits";
import useUnitSearch from "@/views/helpdesk/useUnitSearch";
import { showOrigin } from "@/views/helpdesk/catalogConfig";
import axios from "@axios";
import vSelect from "vue-select";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import FileUploadWithPreview from "@/components/BuildRiskForm/FileUploadWithPreview.vue";
import { getUserData } from "@/auth/utils";

export default {
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    CountedTextarea,
    BFormCheckbox,
    BSpinner,
    BModal,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    formValidation,
    FileUploadWithPreview,
  },
  props: {
    ticket: {
      type: Object,
    },
    modalId: {
      type: String,
      default: "create_ticket_modal",
    },
  },
  data() {
    return {
      textLimits: TEXT_LIMITS,
      required,
      email,
      form: {
        name: "",
        userId: "",
        email: "",
        classification: null,
        failureOriginId: null,
        failureTypeId: null,
        failureSubtypeId: null,
        priority: null,
        message: "",
        file: [],
        // ── Múltiple ──
        unitIds: [], // [13604, 13605]
        hasRelatedCamera: false,
        camerasByUnit: {}, // { 13604: [48077], 13605: [48171] }
      },

      classificationOptions: [
        { value: "siniestro", label: "Siniestro" },
        { value: "incidente", label: "Incidente" },
      ],
      categoryRoots: [],
      loadingCategories: false,
      priorityOptions: [],

      // Opciones de cámaras por unidad y su estado de carga
      camerasByUnitOptions: {}, // { 13604: [ {value,label} ] }
      loadingCamerasByUnit: {}, // { 13604: true/false }

      buttonSave: false,
    };
  },
  computed: {
    withOrigin() {
      return showOrigin(this.form.classification);
    },
    originOptions() {
      return this.categoryRoots.map((root) => ({ value: root.id, label: root.name }));
    },
    typeOptions() {
      return this.categoryRoots
        .filter((root) => !this.withOrigin || root.id === this.form.failureOriginId)
        .flatMap((root) =>
          (root.children_recursive || []).map((type) => ({
            value: type.id,
            label: type.name,
            code: type.code,
            children: type.children_recursive || [],
          }))
        );
    },
    subtypeOptions() {
      const type = this.typeOptions.find((t) => t.value === this.form.failureTypeId);
      return type
        ? type.children.map((sub) => ({ value: sub.id, label: sub.name }))
        : [];
    },
  },
  setup() {
    const toast = useToast();
    const { unitOptions, loadingUnits, searchUnits, onUnitSearch, pinUnits } = useUnitSearch();
    return { toast, unitOptions, loadingUnits, searchUnits, onUnitSearch, pinUnits };
  },
  async mounted() {
    this.setUserData();
    await this.loadPriorities();
  },
  methods: {
    setUserData() {
      const userData = getUserData();
      this.form.name = userData.name;
      this.form.userId = userData.id;
      this.form.email = userData.email;
    },
    showError(title) {
      this.toast({
        component: ToastificationContent,
        props: { title, icon: "AlertTriangleIcon", variant: "danger" },
      });
    },
    async loadPriorities() {
      try {
        const priorities = await store.dispatch("helpdesk/getPriorities");
        const EXCLUDED = ["VERY_LOW", "VERY_HIGH"];
        this.priorityOptions = priorities
          .filter((p) => p.active && !EXCLUDED.includes(p.code))
          .map((p) => ({ value: p.id, label: p.name }));
      } catch (e) {
        this.showError("No se pudieron cargar las prioridades.");
      }
    },
    onShow() {
      if (!this.unitOptions.length) this.searchUnits();
    },
    async onClassificationChange(catalog) {
      this.form.failureOriginId = null;
      this.form.failureTypeId = null;
      this.form.failureSubtypeId = null;
      this.categoryRoots = [];
      if (!catalog) return;

      this.loadingCategories = true;
      try {
        this.categoryRoots = await store.dispatch(
          "helpdesk/getTicketFailureCategories",
          catalog
        );
      } catch (e) {
        this.showError("No se pudo cargar el catálogo.");
      } finally {
        this.loadingCategories = false;
      }
    },
    onOriginChange() {
      this.form.failureTypeId = null;
      this.form.failureSubtypeId = null;
    },

    // ── PMI múltiple ──
    unitLabel(unitId) {
      const found = this.unitOptions.find((u) => u.value === unitId);
      return found ? found.label : `PMI ${unitId}`;
    },
    onUnitsChange(unitIds) {
      (unitIds || []).forEach((id) => this.pinUnits && this.pinUnits(id));

      // Limpia cámaras de unidades que ya no están seleccionadas
      Object.keys(this.form.camerasByUnit).forEach((id) => {
        if (!unitIds.includes(Number(id)) && !unitIds.includes(id)) {
          this.$delete(this.form.camerasByUnit, id);
          this.$delete(this.camerasByUnitOptions, id);
        }
      });

      // Carga cámaras de las nuevas, si el switch está activo
      if (this.form.hasRelatedCamera) {
        unitIds.forEach((id) => this.loadCamerasForUnit(id));
      }
    },
    onToggleCameras(checked) {
      if (!checked) {
        this.form.camerasByUnit = {};
        return;
      }
      this.form.unitIds.forEach((id) => this.loadCamerasForUnit(id));
    },
    async loadCamerasForUnit(unitId) {
      if (this.camerasByUnitOptions[unitId]) return; // ya cargadas
      this.$set(this.loadingCamerasByUnit, unitId, true);
      try {
        const { data } = await axios.get("v1/proxy_camera", {
          params: { units: [unitId], max: 1000 },
        });
        const options = (data.success ? data.data : []).map((c) => ({
          value: c.id,
          label: (c.camera && c.camera.name) || `Cámara ${c.id}`,
        }));
        this.$set(this.camerasByUnitOptions, unitId, options);
        if (!this.form.camerasByUnit[unitId]) {
          this.$set(this.form.camerasByUnit, unitId, []);
        }
      } catch (e) {
        this.showError("No se pudieron cargar las cámaras del PMI.");
      } finally {
        this.$set(this.loadingCamerasByUnit, unitId, false);
      }
    },

    onSubmit() {
      if (!this.form.name || !this.form.email) {
        this.showError("Datos incompletos");
        return;
      }
      this.buttonSave = true;

      const formData = new FormData();
      formData.append("name", this.form.name);
      formData.append("from", this.form.email);
      formData.append("message", this.form.message);
      formData.append("id_priority", this.form.priority);
      formData.append(
        "id_failure_category",
        this.form.failureSubtypeId || this.form.failureTypeId
      );

      // ── units[i][id_unit] + units[i][id_cameras][j] ──
      this.form.unitIds.forEach((unitId, i) => {
        formData.append(`units[${i}][id_unit]`, unitId);
        const cams =
          (this.form.hasRelatedCamera && this.form.camerasByUnit[unitId]) || [];
        cams.forEach((camId, j) => {
          formData.append(`units[${i}][id_cameras][${j}]`, camId);
        });
      });

      this.form.file.forEach((item) => {
        formData.append("attachments[]", item);
      });

      store
        .dispatch("helpdesk/addTicket", formData)
        .then((response) => {
          if (response && response.success) {
            this.toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            this.buttonSave = false;
            this.$emit("refetch-data");
            this.onCancel();
            this.openCreatedTicket(response.data && response.data.id);
          } else {
            this.buttonSave = false;
            this.toast({
              component: ToastificationContent,
              props: {
                title: (response && response.message) || "No se pudo guardar.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((error) => {
          this.buttonSave = false;
          this.toast({
            component: ToastificationContent,
            props: {
              title: error?.response?.data?.message || "Error al guardar.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    },
    onUpdateFiles(payload) {
      this.form.file = payload.rawFiles || [];
    },
    openCreatedTicket(ticketId) {
      store.dispatch("helpdesk/getMyOpenTickets").catch(() => {});
      if (!ticketId) return;
      this.$router
        .push({ name: "helpdesk_ticket_details", params: { id: ticketId } })
        .catch(() => {});
    },
    onCancel() {
      this.$bvModal.hide(this.modalId);
      this.form.classification = null;
      this.form.failureOriginId = null;
      this.form.failureTypeId = null;
      this.form.failureSubtypeId = null;
      this.form.priority = null;
      this.form.message = "";
      this.form.file = [];
      this.form.unitIds = [];
      this.form.hasRelatedCamera = false;
      this.form.camerasByUnit = {};
      this.categoryRoots = [];
      this.camerasByUnitOptions = {};
      this.loadingCamerasByUnit = {};
    },
  },
};
</script>

<style lang="scss" scoped>
/* Pills del v-select (PMI y cámaras múltiples): forzar fondo azul siempre,
   evita que a veces salgan en blanco según la variante del tema. */
::v-deep .vs__selected {
  background-color: #1b3d5e !important;
  border-color: #1b3d5e !important;
  color: #ffffff !important;
}
::v-deep .vs__selected .vs__deselect,
::v-deep .vs__selected .vs__deselect svg,
::v-deep .vs__selected .vs__deselect path {
  fill: #ffffff !important;
  stroke: #ffffff !important;
  opacity: 0.9;
}
::v-deep .vs--disabled .vs__selected {
  background-color: #1b3d5e !important;
  color: #ffffff !important;
  opacity: 0.65;
}
</style>